import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorContent } from '@/components/atoms/ApiErrorMessage/apiErrorContent';
import { ToastError, ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import type { OverviewCage } from '@/domain/client';
import type { MarkHistoricalInput } from '@/domain/locations';

/** What a historical checkbox reads and writes while the pens are being tagged. */
export interface HistoricalSelection {
    isChecked: (cage: OverviewCage) => boolean;
    toggle: (cage: OverviewCage) => void;
    /** The batch is being saved: every box is locked until it settles. */
    isSubmitting: boolean;
}

export interface HistoricalBatch {
    selection: HistoricalSelection;
    /** Pens whose box differs from what is saved. */
    count: number;
    /** Saves the batch as one request; toasts the outcome and keeps the ticks on failure. */
    confirm: () => Promise<void>;
    /** Drops every unconfirmed tick. */
    reset: () => void;
}

type PendingChanges = ReadonlyMap<number, boolean>;

const NO_CHANGES: PendingChanges = new Map();

/**
 * The pens ticked or unticked while tagging, kept locally until the user confirms them as one
 * request. A box put back to what the server says drops out of the batch, so `count` is the
 * number of pens that would actually change.
 *
 * The ticks belong to the location they were made in. The dashboard keeps this table mounted
 * when the user moves to another location, and a batch carried over would mark pens the user
 * never saw there historical and close their groups.
 */
export function useHistoricalSelection(
    locationId: number,
    markHistorical: (changes: MarkHistoricalInput[]) => Promise<unknown>,
    onConfirmed: () => void
): HistoricalBatch {
    const { t } = useTranslation();
    const [ticks, setTicks] = useState<{ locationId: number; pending: PendingChanges }>({
        locationId,
        pending: NO_CHANGES,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const pending = ticks.locationId === locationId ? ticks.pending : NO_CHANGES;

    const isChecked = useCallback(
        (cage: OverviewCage) => pending.get(cage.cageId) ?? cage.historicalSince != null,
        [pending]
    );

    const toggle = useCallback(
        (cage: OverviewCage) => {
            setTicks((current) => {
                const next = new Map(
                    current.locationId === locationId ? current.pending : NO_CHANGES
                );
                const wanted = !(next.get(cage.cageId) ?? cage.historicalSince != null);
                if (wanted === (cage.historicalSince != null)) next.delete(cage.cageId);
                else next.set(cage.cageId, wanted);
                return { locationId, pending: next };
            });
        },
        [locationId]
    );

    const reset = useCallback(() => setTicks({ locationId, pending: NO_CHANGES }), [locationId]);

    const confirm = useCallback(async () => {
        const changes = [...pending].map(([cageId, historical]) => ({ cageId, historical }));
        if (changes.length === 0) return;
        setIsSubmitting(true);
        try {
            await markHistorical(changes);
            ToastSuccess(
                t('organisms.locationOverviewTable.markHistoricalSuccess', {
                    count: changes.length,
                }),
                'mark-historical-success'
            );
            reset();
            onConfirmed();
        } catch (error) {
            ToastError(
                apiErrorContent(error, t('organisms.locationOverviewTable.markHistoricalFailed')),
                'mark-historical-error'
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [pending, markHistorical, onConfirmed, reset, t]);

    const selection: HistoricalSelection = useMemo(
        () => ({ isChecked, toggle, isSubmitting }),
        [isChecked, toggle, isSubmitting]
    );

    return { selection, count: pending.size, confirm, reset };
}
